// src/Chatbot.js
import React, { useState, useRef } from 'react';
import './Chatbot.css';

const Chatbot = ({ onClose }) => {
    // API URL - hardcoded
    const API_URL = 'https://chatbot.geekmacsolutions.com/chat';
    
    // Chat state
    const [userMessage, setUserMessage] = useState('');
    const [messages, setMessages] = useState([
        {
            sender: 'Bot',
            text: 'Welcome to Sri Lankan University Guidance! 🎓 Please select your stream from these options: 1. Arts 2. Commerce 3. Biological Science 4. Physical Science 5. Engineering Technology 6. Biosystems Technology 7. ICT (Technology Stream) 8. Cross-Stream You can either type the number or name of your preferred stream.'
        }
    ]);
    const [subjects, setSubjects] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState(['', '', '']);
    
    // Bot form state
    const [showForm, setShowForm] = useState(true); // Start with form
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        contactNumber: '',
        email: '',
        subjectCombination: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const formRef = useRef(null);

    // Chat functions
    const sendMessage = async () => {
        if (!userMessage.trim()) return;

        const newMessages = [...messages, { sender: 'You', text: userMessage }];
        setMessages(newMessages);

        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message: userMessage }),
            });

            const data = await res.json();

            // Support showing both response text and degrees together if provided
            const botMessage = { sender: 'Bot' };
            if (data && typeof data.response === 'string' && data.response.trim().length > 0) {
                botMessage.text = data.response;
            }
            if (data && Array.isArray(data.degrees) && data.degrees.length > 0) {
                botMessage.degrees = data.degrees;
            }

            setMessages([
                ...newMessages,
                botMessage
            ]);

            setUserMessage('');

            // If subjects are returned
            if (data.subjects && data.subjects.length > 0) {
                setSubjects(data.subjects);
            }

        } catch (error) {
            setMessages([
                ...newMessages,
                { sender: 'Bot', text: 'Error connecting to server.' }
            ]);
        }
    };

    const handleSubjectChange = (index, value) => {
        const updated = [...selectedSubjects];
        updated[index] = value;
        setSelectedSubjects(updated);
    };

    const submitSubjects = async () => {
        if (selectedSubjects.includes('')) {
            alert('Please select 3 subjects before submitting.');
            return;
        }

        const newMessages = [
            ...messages,
            { sender: 'You', text: `Selected subjects: ${selectedSubjects.join(', ')}` }
        ];
        setMessages(newMessages);

        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: 'subjects_selected',
                    subjects: selectedSubjects
                }),
            });

            const data = await res.json();

            // Support both response text and degrees when subjects are submitted
            const botMessage = { sender: 'Bot' };
            if (data && typeof data.response === 'string' && data.response.trim().length > 0) {
                botMessage.text = data.response;
            } else {
                botMessage.text = 'Subjects submitted successfully!';
            }
            if (data && Array.isArray(data.degrees) && data.degrees.length > 0) {
                botMessage.degrees = data.degrees;
            }

            setMessages([
                ...newMessages,
                botMessage
            ]);

            setSubjects([]);
            setSelectedSubjects(['', '', '']);

        } catch (error) {
            setMessages([
                ...newMessages,
                { sender: 'Bot', text: 'Error submitting subjects.' }
            ]);
        }
    };

    // Bot form functions
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear messages when user types
        setSuccessMsg('');
        setErrorMsg('');
    };

    const validateForm = () => {
        const phoneRegex = /^[+]?[\d\s\-()]{10,}$/;
        if (!formData.firstName.trim()) return 'Please enter your first name';
        if (!formData.lastName.trim()) return 'Please enter your last name';
        if (!formData.contactNumber.trim() || !phoneRegex.test(formData.contactNumber)) return 'Please enter a valid contact number';
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email address';
        if (!formData.subjectCombination.trim()) return 'Please enter your subject combination';
        return '';
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSuccessMsg('');
        setErrorMsg('');
        
        const error = validateForm();
        if (error) {
            setErrorMsg(error);
            return;
        }
        
        if (!window.emailjs) {
            setErrorMsg('Unable to send right now. Please try again later.');
            return;
        }
        
        setSubmitting(true);
        try {
            // Ensure hidden fields are set in the form
            if (formRef.current) {
                const formEl = formRef.current;
                const dateEl = formEl.querySelector('#date');
                if (dateEl) dateEl.value = new Date().toLocaleString();
            }
            
            const serviceId = 'service_qkppwaq';
            const templateId = 'template_m9o5qov';
            await window.emailjs.sendForm(serviceId, templateId, formRef.current);
            setSuccessMsg('Message sent successfully! One of our agents will get back to you.');
            
            // Reset form
            setFormData({
                firstName: '',
                lastName: '',
                contactNumber: '',
                email: '',
                subjectCombination: '',
                message: ''
            });
            
            // Switch to chat interface immediately
            setShowForm(false);
            setSuccessMsg('');
            
        } catch (err) {
            setErrorMsg('Failed to send message. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="chatbox">
            {/* Header with close button */}
            <div className="chatbox-header">
                <h3 className="chatbox-title">UGC Course Guide Bot</h3>
                <button className="close-btn" onClick={onClose} title="Close Chat">
                    ×
                </button>
            </div>

            {showForm ? (
                /* Bot Form */
                <div className="bot-form-container">
                    <div className="bot-form-header">
                        <h4>UGC Course Inquiry Form</h4>
                        <p className="consultant-message">One of our consultants will get back to you. Please fill out the form below.</p>
                    </div>
                    
                    {successMsg && (
                        <div className="alert alert-success" role="alert">
                            {successMsg}
                        </div>
                    )}
                    {errorMsg && (
                        <div className="alert alert-danger" role="alert">
                            {errorMsg}
                        </div>
                    )}

                    <form ref={formRef} onSubmit={handleFormSubmit} className="bot-form">
                        <div className="form-group">
                            <label htmlFor="firstName" className="required-field">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleFormChange}
                                placeholder="Enter first name"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="lastName" className="required-field">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleFormChange}
                                placeholder="Enter last name"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="contactNumber" className="required-field">Contact Number</label>
                            <input
                                type="tel"
                                id="contactNumber"
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleFormChange}
                                placeholder="Enter contact number"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="email" className="required-field">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleFormChange}
                                placeholder="Enter email"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="subjectCombination" className="required-field">Subject Combination</label>
                            <input
                                type="text"
                                id="subjectCombination"
                                name="subjectCombination"
                                value={formData.subjectCombination}
                                onChange={handleFormChange}
                                placeholder="e.g., Physics, Chemistry, Biology"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="message">Message</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleFormChange}
                                placeholder="Enter your message (optional)"
                                rows="3"
                            />
                        </div>
                        
                        {/* Hidden fields for EmailJS */}
                        <input type="hidden" id="date" name="date" />
                        <input type="hidden" id="inquiryType" name="inquiryType" value="UGC Course Guide" />
                        
                        <div className="form-actions">
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={submitting}
                            >
                                {submitting ? 'Sending...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                /* Chat Interface */
                <>
                    <div className="messages">
                        {messages.map((msg, index) => (
                            <div key={index} className="message-item">
                                <div className="message-sender">{msg.sender}:</div>
                                <div className="message-content">
                                    {/* Render text if available */}
                                    {typeof msg.text === 'string' && msg.text.trim().length > 0 && (
                                        <div className="bot-text">
                                            <span>{msg.text}</span>
                                        </div>
                                    )}
                                    {/* Render degrees in cards if available */}
                                    {Array.isArray(msg.degrees) && msg.degrees.length > 0 && (
                                        <div className="degree-list">
                                            {msg.degrees.map((degree, i) => (
                                                <div key={i} className="degree-card">
                                                    <h6>{degree.degree}</h6>
                                                    <ul>
                                                        {degree.universities.map((uni, j) => (
                                                            <li key={j}>{uni}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Show dropdowns if subjects exist */}
                        {subjects.length > 0 && (
                            <div className="subject-selection">
                                <p>Select 3 subjects:</p>
                                <div className="subject-dropdowns">
                                    {[0, 1, 2].map((i) => (
                                        <select
                                            key={i}
                                            value={selectedSubjects[i]}
                                            onChange={(e) => handleSubjectChange(i, e.target.value)}
                                        >
                                            <option value="">-- Select Subject --</option>
                                            {subjects.map((subj, idx) => (
                                                <option key={idx} value={subj}>{subj}</option>
                                            ))}
                                        </select>
                                    ))}
                                </div>
                                <button
                                    className="submit-subjects-btn"
                                    onClick={submitSubjects}
                                >
                                    Submit Subjects
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="input">
                        <input
                            type="text"
                            placeholder="Ask About Degree Programs"
                            value={userMessage}
                            onChange={(e) => setUserMessage(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <button
                            className="send-btn"
                            onClick={sendMessage}
                        >
                            Send
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Chatbot; 